import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Tabs,
  Tab,
  Alert
} from '@mui/material';
import {
  Groups,
  AccessTime,
  CheckCircle,
  History,
  Refresh,
  CalendarToday
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import { GRADIENTS, RADIUS } from '../../theme/tokens';

const MotionCard = motion(Card);
const MotionBox = motion(Box);

/**
 * Librarian Sessions - Mobile-First Sessions View
 */
function LibrarianSessions({ showSnackbar }) {
  const { user } = useContext(AuthContext);

  const [library, setLibrary] = useState(null);
  const [activeSessions, setActiveSessions] = useState([]);
  const [pastSessions, setPastSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);

  useEffect(() => {
    fetchSessionsData();
  }, []);

  const fetchSessionsData = async () => {
    try {
      setLoading(true);

      // Fetch librarian's assigned library
      const libraryResponse = await api.get('/librarian/library');

      if (!libraryResponse.data.data.library) {
        showSnackbar('No library assigned', 'error');
        setLoading(false);
        return;
      }

      const myLibrary = libraryResponse.data.data.library;
      setLibrary(myLibrary);

      // Fetch active sessions
      try {
        const activeResponse = await api.get('/librarian/sessions', {
          params: { library_id: myLibrary.id, active: true }
        });
        setActiveSessions(activeResponse.data.data.sessions || []);
      } catch (err) {
        setActiveSessions([]);
      }

      // Fetch past sessions
      try {
        const pastResponse = await api.get('/librarian/sessions', {
          params: { library_id: myLibrary.id, active: false }
        });
        setPastSessions(pastResponse.data.data.sessions || []);
      } catch (err) {
        setPastSessions([]);
      }
    } catch (err) {
      console.error('Error fetching sessions:', err);
      showSnackbar('Failed to load sessions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (enteredAt, exitedAt) => {
    const enter = new Date(enteredAt);
    const exit = exitedAt ? new Date(exitedAt) : new Date();
    const diffMs = exit - enter;
    const minutes = Math.floor(diffMs / 1000 / 60);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${minutes}m`;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography>Loading sessions...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'transparent' }}>
      <Container maxWidth="sm" sx={{ py: 2, pb: 10 }}>
        <MotionBox
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Header */}
          <MotionBox variants={itemVariants} mb={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h4" fontWeight="800" gutterBottom>
                  Sessions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Track student check-ins and check-outs
                </Typography>
              </Box>
              <IconButton
                onClick={fetchSessionsData}
                sx={{
                  bgcolor: 'action.hover',
                  '&:hover': { bgcolor: 'action.selected' }
                }}
              >
                <Refresh />
              </IconButton>
            </Box>
          </MotionBox>

          {/* Stats */}
          <MotionCard
            variants={itemVariants}
            sx={{
              borderRadius: RADIUS.xl,
              mb: 2,
              background: GRADIENTS.coin,
              color: 'white'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-around" textAlign="center">
                <Box>
                  <Typography variant="h3" fontWeight="800">
                    {activeSessions.length}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Active Now
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.3)' }} />
                <Box>
                  <Typography variant="h3" fontWeight="800">
                    {pastSessions.length}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Completed
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.3)' }} />
                <Box>
                  <Typography variant="h3" fontWeight="800">
                    {library?.capacity || 0}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Total Seats
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </MotionCard>

          {/* Tabs */}
          <MotionBox variants={itemVariants} mb={2}>
            <Tabs
              value={currentTab}
              onChange={(e, newValue) => setCurrentTab(newValue)}
              variant="fullWidth"
              sx={{
                bgcolor: 'background.paper',
                borderRadius: RADIUS.lg,
                p: 0.5,
                '& .MuiTabs-indicator': {
                  display: 'none'
                },
                '& .MuiTab-root': {
                  borderRadius: RADIUS.md,
                  textTransform: 'none',
                  fontWeight: 600,
                  transition: 'all 0.3s ease',
                  '&.Mui-selected': {
                    background: GRADIENTS.brand,
                    color: 'white'
                  }
                }
              }}
            >
              <Tab icon={<Groups fontSize="small" />} label={`Active (${activeSessions.length})`} iconPosition="start" />
              <Tab icon={<History fontSize="small" />} label={`Past (${pastSessions.length})`} iconPosition="start" />
            </Tabs>
          </MotionBox>

          {/* Content */}
          <AnimatePresence mode="wait">
            {currentTab === 0 ? (
              <MotionCard
                key="active"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
                sx={{ borderRadius: RADIUS.xl }}
              >
                <CardContent sx={{ p: 0 }}>
                  {activeSessions.length === 0 ? (
                    <Box p={6} textAlign="center">
                      <Groups sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No Active Sessions
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        No students are currently checked in
                      </Typography>
                    </Box>
                  ) : (
                    <List disablePadding>
                      {activeSessions.map((session, index) => (
                        <React.Fragment key={session.id}>
                          {index > 0 && <Divider />}
                          <ListItem sx={{ py: 2, px: 2 }}>
                            <ListItemAvatar>
                              <Avatar
                                sx={{
                                  width: 50,
                                  height: 50,
                                  background: 'linear-gradient(135deg, #4A90A4 0%, #2D6B7D 100%)',
                                  fontWeight: 700,
                                }}
                              >
                                {session.student?.name?.charAt(0).toUpperCase()}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography fontWeight="700" variant="body1">
                                  {session.student?.name || 'Unknown'}
                                </Typography>
                              }
                              secondary={
                                <Box mt={0.5}>
                                  <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                    <AccessTime fontSize="small" color="action" />
                                    <Typography variant="caption" color="text.secondary">
                                      Entered: {new Date(session.entered_at).toLocaleTimeString('en-IN', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </Typography>
                                  </Box>
                                  <Box display="flex" alignItems="center" gap={0.5}>
                                    <CalendarToday fontSize="small" color="action" />
                                    <Typography variant="caption" color="text.secondary">
                                      Duration: {formatDuration(session.entered_at, null)}
                                    </Typography>
                                  </Box>
                                </Box>
                              }
                            />
                            <Box>
                              <Chip
                                label="Active"
                                color="success"
                                size="small"
                                icon={<CheckCircle />}
                                sx={{ fontWeight: 600 }}
                              />
                            </Box>
                          </ListItem>
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </CardContent>
              </MotionCard>
            ) : (
              <MotionCard
                key="past"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                sx={{ borderRadius: RADIUS.xl }}
              >
                <CardContent sx={{ p: 0 }}>
                  {pastSessions.length === 0 ? (
                    <Box p={6} textAlign="center">
                      <History sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No Past Sessions
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Completed sessions will appear here
                      </Typography>
                    </Box>
                  ) : (
                    <List disablePadding>
                      {pastSessions.map((session, index) => (
                        <React.Fragment key={session.id}>
                          {index > 0 && <Divider />}
                          <ListItem sx={{ py: 2, px: 2 }}>
                            <ListItemAvatar>
                              <Avatar
                                sx={{
                                  width: 50,
                                  height: 50,
                                  background: GRADIENTS.brand,
                                  fontWeight: 700
                                }}
                              >
                                {session.student?.name?.charAt(0).toUpperCase()}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography fontWeight="700" variant="body1">
                                  {session.student?.name || 'Unknown'}
                                </Typography>
                              }
                              secondary={
                                <Box mt={0.5}>
                                  <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                    <AccessTime fontSize="small" color="action" />
                                    <Typography variant="caption" color="text.secondary">
                                      {new Date(session.entered_at).toLocaleDateString('en-IN', {
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </Typography>
                                  </Box>
                                  <Box display="flex" alignItems="center" gap={0.5}>
                                    <CalendarToday fontSize="small" color="action" />
                                    <Typography variant="caption" color="text.secondary">
                                      Duration: {formatDuration(session.entered_at, session.exited_at)}
                                    </Typography>
                                  </Box>
                                </Box>
                              }
                            />
                            <Box>
                              <Chip
                                label="Completed"
                                color="default"
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </Box>
                          </ListItem>
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </CardContent>
              </MotionCard>
            )}
          </AnimatePresence>
        </MotionBox>
      </Container>
    </Box>
  );
}

export default LibrarianSessions;
